import { __extends } from "tslib";
import { PanoramaToCubeMapTools } from "../../Misc/HighDynamicRange/panoramaToCubemap.js";
import { BaseTexture } from "./baseTexture.js";
import { Texture } from "./texture.js";
import { Tools } from "../../Misc/tools.js";
import "../../Engines/Extensions/engine.rawTexture.js";

import { LoadImage } from "../../Misc/fileTools.js";
/**
 * This represents a texture coming from an equirectangular image supported by the web browser canvas.
 */
var EquiRectangularCubeTexture = /** @class */ (function (_super) {
    __extends(EquiRectangularCubeTexture, _super);
    /**
     * Instantiates an EquiRectangularCubeTexture from the following parameters.
     * @param url The location of the image
     * @param scene The scene the texture will be used in
     * @param size The cubemap desired size (the more it increases the longer the generation will be)
     * @param noMipmap Forces to not generate the mipmap if true
     * @param gammaSpace Specifies if the texture will be used in gamma or linear space
     * (the PBR material requires those textures in linear space, but the standard material would require them in Gamma space)
     * @param onLoad — defines a callback called when texture is loaded
     * @param onError — defines a callback called if there is an error
     */
    function EquiRectangularCubeTexture(url, scene, size, noMipmap, gammaSpace, onLoad, onError) {
        if (noMipmap === void 0) { noMipmap = false; }
        if (gammaSpace === void 0) { gammaSpace = true; }
        if (onLoad === void 0) { onLoad = null; }
        if (onError === void 0) { onError = null; }
        var _this = _super.call(this, scene) || this;
        _this._onLoad = null;
        _this._onError = null;
        if (!url) {
            throw new Error("Image url is not set");
        }
        _this._coordinatesMode = Texture.CUBIC_MODE;
        _this.name = url;
        _this.url = url;
        _this._size = size;
        _this._noMipmap = noMipmap;
        _this.gammaSpace = gammaSpace;
        _this._onLoad = onLoad;
        _this._onError = onError;
        _this.hasAlpha = false;
        _this.isCube = true;
        _this._texture = _this._getFromCache(url, _this._noMipmap, undefined, undefined, undefined, _this.isCube);
        if (!_this._texture) {
            if (!scene.useDelayedTextureLoading) {
                _this._loadImage(_this._loadTexture.bind(_this), _this._onError);
            }
            else {
                _this.delayLoadState = 4;
            }
        }
        else if (onLoad) {
            if (_this._texture.isReady) {
                Tools.SetImmediate(function () { return onLoad(); });
            }
            else {
                _this._texture.onLoadedObservable.add(onLoad);
            }
        }
        return _this;
    }
    /**
     * Load the image data, by putting the image on a canvas and extracting its buffer.
     * @param loadTextureCallback
     * @param onError
     */
    EquiRectangularCubeTexture.prototype._loadImage = function (loadTextureCallback, onError) {
        var _this = this;
        var canvas = document.createElement("canvas");
        LoadImage(this.url, function (image) {
            _this._width = image.width;
            _this._height = image.height;
            canvas.width = _this._width;
            canvas.height = _this._height;
            var ctx = canvas.getContext("2d");
            ctx.drawImage(image, 0, 0);
            var imageData = ctx.getImageData(0, 0, image.width, image.height);
            _this._buffer = imageData.data.buffer;
            canvas.remove();
            loadTextureCallback();
        }, function (_, e) {
            if (onError) {
                onError("".concat(_this.getClassName(), " could not be loaded"), e);
            }
        }, null);
    };
    /**
     * Convert the image buffer into a cubemap and create a CubeTexture.
     */
    EquiRectangularCubeTexture.prototype._loadTexture = function () {
        var _this = this;
        var scene = this.getScene();
        var callback = function () {
            var imageData = _this._getFloat32ArrayFromArrayBuffer(_this._buffer);
            // Extract the raw linear data.
            var data = PanoramaToCubeMapTools.ConvertPanoramaToCubemap(imageData, _this._width, _this._height, _this._size);
            var results = [];
            // Push each faces.
            for (var i = 0; i < 6; i++) {
                var dataFace = data[EquiRectangularCubeTexture._FacesMapping[i]];
                results.push(dataFace);
            }
            return results;
        };
        if (!scene) {
            return;
        }
        this._texture = scene
            .getEngine()
            .createRawCubeTextureFromUrl(this.url, scene, this._size, 4, scene.getEngine().getCaps().textureFloat ? 1 : 7, this._noMipmap, callback, null, this._onLoad, this._onError);
    };
    /**
     * Convert the ArrayBuffer into a Float32Array and drop the transparency channel.
     * @param buffer The ArrayBuffer that should be converted.
     * @returns The buffer as Float32Array.
     */
    EquiRectangularCubeTexture.prototype._getFloat32ArrayFromArrayBuffer = function (buffer) {
        var dataView = new DataView(buffer);
        var floatImageData = new Float32Array((buffer.byteLength * 3) / 4);
        var k = 0;
        for (var i = 0; i < buffer.byteLength; i++) {
            // We drop the transparency channel, because we do not need/want it
            if ((i + 1) % 4 !== 0) {
                floatImageData[k++] = dataView.getUint8(i) / 255;
            }
        }
        return floatImageData;
    };
    /**
     * Get the current class name of the texture useful for serialization or dynamic coding.
     * @returns "EquiRectangularCubeTexture"
     */
    EquiRectangularCubeTexture.prototype.getClassName = function () {
        return "EquiRectangularCubeTexture";
    };
    /**
     * Create a clone of the current EquiRectangularCubeTexture and return it.
     * @returns A clone of the current EquiRectangularCubeTexture.
     */
    EquiRectangularCubeTexture.prototype.clone = function () {
        var scene = this.getScene();
        if (!scene) {
            return this;
        }
        var newTexture = new EquiRectangularCubeTexture(this.url, scene, this._size, this._noMipmap, this.gammaSpace);
        // Base texture
        newTexture.level = this.level;
        newTexture.wrapU = this.wrapU;
        newTexture.wrapV = this.wrapV;
        newTexture.coordinatesIndex = this.coordinatesIndex;
        newTexture.coordinatesMode = this.coordinatesMode;
        return newTexture;
    };
    /** The six faces of the cube. */
    EquiRectangularCubeTexture._FacesMapping = ["right", "left", "up", "down", "front", "back"];
    return EquiRectangularCubeTexture;
}(BaseTexture));
export { EquiRectangularCubeTexture };
//# sourceMappingURL=equiRectangularCubeTexture.js.map